import { useCallback, useEffect, useRef, useState } from "react";

export const NOTIFICATION_DISPLAY_MS = 6000;
export const NOTIFICATION_GAP_MS = 500;

const STORAGE_PREFIX = "notificationSeenKeys:";
// The poll runs continuously, so the seen-key set would otherwise grow forever
// (slowing every persist and eventually hitting the localStorage quota). Keep
// only the most recent keys; anything older than this is extremely unlikely to
// still be returned by the poll endpoint.
const MAX_SEEN_KEYS = 500;
const seenKeysByScope = new Map<string, Set<string>>();

function storageKey(scope: string): string {
    return `${STORAGE_PREFIX}${scope}`;
}

function addSeenKey(seen: Set<string>, key: string) {
    seen.add(key);
    // Sets iterate in insertion order, so the first entry is always the oldest.
    while (seen.size > MAX_SEEN_KEYS) {
        const oldest = seen.values().next().value;
        if (oldest === undefined) break;
        seen.delete(oldest);
    }
}

function getSeenKeysForScope(scope: string): Set<string> {
    let seen = seenKeysByScope.get(scope);
    if (!seen) {
        // Hydrate from localStorage so a page reload doesn't replay awards the
        // poll endpoint still returns.
        try {
            const stored = localStorage.getItem(storageKey(scope));
            const parsed = stored ? JSON.parse(stored) : [];
            // Keep only the most recent keys when an older, uncapped value was
            // persisted before MAX_SEEN_KEYS existed.
            const bounded = Array.isArray(parsed)
                ? parsed.slice(-MAX_SEEN_KEYS)
                : [];
            seen = new Set(bounded);
        } catch {
            seen = new Set();
        }
        seenKeysByScope.set(scope, seen);
    }
    return seen;
}

function persistSeenKeys(scope: string, seen: Set<string>) {
    try {
        localStorage.setItem(storageKey(scope), JSON.stringify(Array.from(seen)));
    } catch {
        // Storage unavailable (e.g. private mode); in-memory dedupe still applies.
    }
}

/** Clears dedupe state for a scope. Intended for unit tests. */
export function resetNotificationQueueDedupe(scope = "default") {
    seenKeysByScope.delete(scope);
    try {
        localStorage.removeItem(storageKey(scope));
    } catch {
        // ignore
    }
}

/** Clears all dedupe state. Call on logout so the next session can show popups again. */
export function clearAllNotificationQueueDedupe() {
    seenKeysByScope.clear();
    try {
        Object.keys(localStorage)
            .filter((key) => key.startsWith(STORAGE_PREFIX))
            .forEach((key) => localStorage.removeItem(key));
    } catch {
        // ignore
    }
}

/**
 * FIFO queue for sequentially showing notification popups.
 *
 * - `enqueue(items)` appends items whose key is not already shown or pending.
 * - The active item auto-dismisses after `NOTIFICATION_DISPLAY_MS`.
 * - `dismiss()` closes the active popup early.
 * - After any dismissal the next item appears `NOTIFICATION_GAP_MS` later.
 * - `dedupeScope` keeps shown keys outside the component lifecycle.
 */
export function useNotificationQueue<T>(
    getKey?: (item: T) => string,
    dedupeScope = "default",
) {
    const [current, setCurrent] = useState<T | null>(null);

    const queueRef = useRef<T[]>([]);
    const currentRef = useRef<T | null>(null);
    const displayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const gapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const getItemKey = useCallback(
        (item: T) => (getKey ? getKey(item) : JSON.stringify(item)),
        [getKey],
    );

    const setActive = useCallback((next: T | null) => {
        currentRef.current = next;
        setCurrent(next);
    }, []);

    const clearTimers = useCallback(() => {
        if (displayTimerRef.current) {
            clearTimeout(displayTimerRef.current);
            displayTimerRef.current = null;
        }
        if (gapTimerRef.current) {
            clearTimeout(gapTimerRef.current);
            gapTimerRef.current = null;
        }
    }, []);

    const getPendingKeys = useCallback(() => {
        const pending = new Set<string>();
        const active = currentRef.current;
        if (active) pending.add(getItemKey(active));
        for (const queued of queueRef.current) {
            pending.add(getItemKey(queued));
        }
        return pending;
    }, [getItemKey]);

    const markKeyShown = useCallback(
        (item: T) => {
            const seen = getSeenKeysForScope(dedupeScope);
            addSeenKey(seen, getItemKey(item));
            persistSeenKeys(dedupeScope, seen);
        },
        [dedupeScope, getItemKey],
    );

    const showNext = useCallback(() => {
        gapTimerRef.current = null;
        const next = queueRef.current.shift() ?? null;
        setActive(next);
        if (!next) return;

        markKeyShown(next);

        displayTimerRef.current = setTimeout(() => {
            displayTimerRef.current = null;
            setActive(null);
            gapTimerRef.current = setTimeout(showNext, NOTIFICATION_GAP_MS);
        }, NOTIFICATION_DISPLAY_MS);
    }, [markKeyShown, setActive]);

    const enqueue = useCallback(
        (items: readonly T[] | null | undefined) => {
            if (!items?.length) return;

            const seen = getSeenKeysForScope(dedupeScope);
            const pending = getPendingKeys();
            const batchKeys = new Set<string>();

            const newItems = items.filter((item) => {
                const key = getItemKey(item);
                if (seen.has(key) || pending.has(key) || batchKeys.has(key)) {
                    return false;
                }
                batchKeys.add(key);
                return true;
            });

            if (!newItems.length) return;

            queueRef.current.push(...newItems);
            const idle = currentRef.current === null && gapTimerRef.current === null;
            if (idle) showNext();
        },
        [dedupeScope, getItemKey, getPendingKeys, showNext],
    );

    const dismiss = useCallback(() => {
        if (currentRef.current === null) return;
        clearTimers();
        setActive(null);
        gapTimerRef.current = setTimeout(showNext, NOTIFICATION_GAP_MS);
    }, [clearTimers, setActive, showNext]);

    useEffect(() => () => clearTimers(), [clearTimers]);

    return { current, enqueue, dismiss };
}
